/* The ownership-verification GATE TABLE (Tier-1 C1, 2026-09-04).
 *
 * The v13 OV card (locked 2026-08-31) shows the four conditions policy
 * demands of a document — right object · accepted source · fully signed ·
 * fresh enough — and "confirms the record" last. C1 turns those sentence
 * rows into a TABLE: gate · finding · document value / record value · page
 * door, and lets a gate FAIL on the page: the failing row goes rust with
 * the compared pair ("document 9,500 · record 8,000"), and when any gate
 * of a mandatory demand fails the primary verb flips to "Reject — falls
 * short" ("Approve anyway…" stays, secondary, with a reason recorded as an
 * override).
 *
 * Pure module (no React): every gate is derived from what
 * GET /api/evidence-status/{id} already carries — proof[] (document_value
 * / record_value / source_page), signature_blocks, document_date vs
 * freshness_cutoff, claim_source_type vs accepted_sources. Unit-tested in
 * tests/e2e/evidence-approval.spec.ts (pure part). */

/** Value-level equality (numeric readings compare by value, not
 * representation) — the same rule lib/approveDiff.sameValue applies,
 * kept local so the pure module compiles alone for the unit lane. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  const na = Number(String(a).replace(/[,\s]/g, ''))
  const nb = Number(String(b).replace(/[,\s]/g, ''))
  if (
    Number.isFinite(na) &&
    Number.isFinite(nb) &&
    String(a).trim() !== '' &&
    String(b).trim() !== ''
  )
    return na === nb
  return String(a) === String(b)
}

export interface OvProofRow {
  field: string
  document_value: string | null
  record_value: string | null
  source_page?: number | null
  source_snippet?: string | null
}

export interface OvRowLike {
  object_type: string
  obligation: {
    is_mandatory: boolean
    accepted_sources: string[]
  } | null
  proof: OvProofRow[]
  document_date?: string | null
  document_type?: string | null
  claim_source_type?: string | null
  freshness_cutoff?: string | null
  signature_blocks?: { present?: boolean; unsigned?: number | null } | null
}

export type OvGateKey = 'object' | 'source' | 'signed' | 'fresh' | 'confirms'

export interface OvGate {
  key: OvGateKey
  /** Which proof field this row is about (confirms rows only). */
  field?: string
  ok: boolean
  /** Values for the document / record column — a compared pair, or a
   * single document-side value (record null), or nothing. */
  document: string | null
  record: string | null
  /** Page door target (null = the document without a page). */
  page: number | null
  /** The ✗ finding, in officer words; null when the gate holds (the
   * component composes the ✓ finding from the row's own facts). */
  failing: string | null
}

/** Compare policy authority categories, never document-type wording. */
export function sourceMatches(source: string, claimSourceType: string | null | undefined): boolean {
  if (!claimSourceType) return false
  return source.trim().toUpperCase() === claimSourceType.trim().toUpperCase()
}

function num(v: string | null): number | null {
  if (v == null) return null
  const n = Number(String(v).replace(/[,\s]/g, ''))
  return Number.isFinite(n) ? n : null
}

function fmtDelta(n: number): string {
  return new Intl.NumberFormat('en', { maximumFractionDigits: 6 }).format(n)
}

/** The ✗ finding for a field the document and the record disagree on. */
export function disagreementFinding(field: string, documentValue: string, recordValue: string) {
  const word = field.replace(/_/g, ' ')
  const d = num(documentValue)
  const r = num(recordValue)
  if (d != null && r != null && d !== r) {
    const diff = d - r
    return `${word} disagree — ${fmtDelta(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} than the record holds`
  }
  return `${word} disagree — the document states one value, the record holds another`
}

/** Evaluate every gate the row can show. Rows the read model cannot
 * judge (no signature facts, no cutoff, no proof) are OMITTED — an
 * unknown is never rendered as ✓. */
export function evaluateGates(row: OvRowLike): OvGate[] {
  const proved = row.proof.filter((r) => r.document_value != null)
  const firstPage = proved.find((r) => r.source_page)?.source_page ?? null
  const gates: OvGate[] = []

  // 1 — about the right object: the document reached this row's object
  // through the gate that minted the sign-off; the row itself is the
  // proof. (A wrong-object document never mints a sign-off task.)
  gates.push({
    key: 'object',
    ok: true,
    document: null,
    record: null,
    page: firstPage,
    failing: null,
  })

  // 2 — SOURCE compares the document's authority category to policy.
  // A missing classification is unknown, so this gate is omitted.
  const sources = row.obligation?.accepted_sources ?? []
  if (sources.length > 0 && row.claim_source_type) {
    const lit = sources.some((s) => sourceMatches(s, row.claim_source_type))
    gates.push({
      key: 'source',
      ok: lit,
      document: row.claim_source_type.replace(/_/g, ' '),
      record: null,
      page: null,
      failing: lit ? null : 'not a source policy accepts for this demand',
    })
  }

  // 3 — fully signed: counted from the persisted facts.
  const sig = row.signature_blocks ?? null
  if (sig?.present) {
    const unsigned = sig.unsigned ?? null
    const ok = unsigned == null || unsigned === 0
    gates.push({
      key: 'signed',
      ok,
      document: null,
      record: null,
      page: null,
      failing: ok ? null : `${unsigned} signature space${unsigned === 1 ? '' : 's'} unsigned`,
    })
  }

  // 4 — fresh enough: the document's own date against the cutoff.
  if (row.freshness_cutoff) {
    const docDate = row.document_date ?? null
    const ok = docDate == null || docDate >= row.freshness_cutoff
    gates.push({
      key: 'fresh',
      ok,
      document: docDate,
      record: row.freshness_cutoff,
      page: firstPage,
      failing: ok ? null : 'dated before the cutoff policy allows',
    })
  }

  // 5 — confirms the record, LAST: one row per compared field, ✗ where
  // the document and the record disagree.
  if (proved.length === 0) {
    gates.push({
      key: 'confirms',
      ok: true,
      document: null,
      record: null,
      page: firstPage,
      failing: null,
    })
  } else {
    for (const r of proved) {
      const agrees = r.record_value == null || sameValue(r.document_value, r.record_value)
      gates.push({
        key: 'confirms',
        field: r.field,
        ok: agrees,
        document: r.document_value,
        record: r.record_value,
        page: r.source_page ?? firstPage,
        failing: agrees
          ? null
          : disagreementFinding(r.field, r.document_value as string, r.record_value as string),
      })
    }
  }
  return gates
}

/** Does a failing gate flip the primary verb? Only a MANDATORY demand's
 * gate failure does — a recommended demand's document falling short is
 * the officer's judgement call, not a refusal. */
export function primaryFlips(row: OvRowLike, gates: OvGate[]): boolean {
  const mandatory = row.obligation?.is_mandatory ?? true
  return mandatory && gates.some((g) => !g.ok)
}
